import React, { useState, useEffect } from 'react';
import { useTheme } from '@context/ThemeContext';
import { useToast } from '@hooks/useToast';

interface UserProfile {
  username: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  profilePic?: string;
  dob: string;
  gender: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  classOrStandard: string;
}

interface ProfileSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (profile: UserProfile) => Promise<void>;
  initialData?: UserProfile;
  mode?: 'view' | 'create'; // 'view' for account settings, 'create' for new user
}

export const ProfileSettingsModal: React.FC<ProfileSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  mode = 'view',
}) => {
  const { themeConfig: tc } = useTheme();
  const { showToast } = useToast();
  // Theme-token class strings (this modal was previously hard-coded light).
  const labelCls = `block text-sm font-semibold ${tc.layout.text} mb-1.5`;
  const labelClsLg = `block text-sm font-semibold ${tc.layout.text} mb-2`;
  const inputCls = `w-full px-3.5 py-2 text-sm border rounded-lg ${tc.layout.cardBorder} ${tc.layout.contentBackground} ${tc.layout.text} placeholder:${tc.layout.textMuted} focus:outline-none focus:ring-2 focus:ring-amber-500`;
  const selectCls = inputCls;
  const inputReadonlyCls = `w-full px-3.5 py-2 text-sm border rounded-lg ${tc.layout.cardBorder} ${tc.layout.background} ${tc.layout.textSecondary} cursor-not-allowed`;
  const hintCls = `text-xs ${tc.layout.textMuted} mt-1`;

  const [profile, setProfile] = useState<UserProfile>(
    initialData || {
      username: '',
      email: '',
      phone: '',
      firstName: '',
      lastName: '',
      profilePic: '',
      dob: '',
      gender: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      classOrStandard: '',
    }
  );

  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profilePicPreview, setProfilePicPreview] = useState<string | null>(
    initialData?.profilePic || null
  );

  // Sync initialData changes with profile state
  useEffect(() => {
    if (initialData) {
      setProfile(initialData);
      setProfilePicPreview(initialData.profilePic || null);
      console.log('[DEBUG] ProfileSettingsModal initialized with data:', initialData);
    }
  }, [
    initialData?.email,
    initialData?.firstName,
    initialData?.lastName,
    initialData?.phone,
    initialData?.dob,
    initialData?.gender,
    initialData?.address,
    initialData?.city,
    initialData?.state,
    initialData?.zipCode,
    initialData?.country,
    initialData?.classOrStandard,
    initialData?.profilePic,
  ]); // Dependency on all fields to detect changes

  const handleProfileChange = (field: keyof UserProfile, value: any) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handlePasswordChange = (field: keyof typeof passwords, value: string) => {
    setPasswords((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setProfilePicPreview(result);
        setProfile((prev) => ({ ...prev, profilePic: result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateProfileForm = (): boolean => {
    if (!profile.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!profile.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    
    // For create mode, additional fields are required
    if (mode === 'create') {
      if (!profile.phone.trim()) {
        setError('Phone number is required');
        return false;
      }
      if (!profile.dob) {
        setError('Date of birth is required');
        return false;
      }
      if (!profile.gender) {
        setError('Gender is required');
        return false;
      }
      if (!profile.address.trim()) {
        setError('Address is required');
        return false;
      }
      if (!profile.city.trim()) {
        setError('City is required');
        return false;
      }
      if (!profile.classOrStandard) {
        setError('Class/Standard is required');
        return false;
      }
      if (!passwords.newPassword.trim()) {
        setError('Password is required');
        return false;
      }
      if (passwords.newPassword.length < 6) {
        setError('Password must be at least 6 characters');
        return false;
      }
      if (!passwords.confirmPassword.trim()) {
        setError('Confirm password is required');
        return false;
      }
      if (passwords.newPassword !== passwords.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }
    
    return true;
  };

  const validatePasswordForm = (): boolean => {
    // Only validate if the user is trying to change password (at least one password field is filled)
    const hasPasswordField = passwords.currentPassword.trim() || passwords.newPassword.trim() || passwords.confirmPassword.trim();
    
    if (!hasPasswordField) {
      // If no password fields are filled, that's ok - password change is optional
      return true;
    }
    
    // If user starts filling in password, all password fields become required
    if (mode === 'view') {
      if (!passwords.currentPassword.trim()) {
        setError('Current password is required');
        return false;
      }
      if (!passwords.newPassword.trim()) {
        setError('New password is required');
        return false;
      }
      if (passwords.newPassword.length < 6) {
        setError('New password must be at least 6 characters');
        return false;
      }
      if (passwords.newPassword === passwords.currentPassword) {
        setError('New password cannot be the same as your old password');
        return false;
      }
      if (!passwords.confirmPassword.trim()) {
        setError('Confirm password is required');
        return false;
      }
      if (passwords.newPassword !== passwords.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }
    return true;
  };

  const handleSaveProfile = async () => {
    if (!validateProfileForm()) return;

    setIsLoading(true);
    setError(null);
    try {
      // Profile Information is its own tab, scoped to profile fields only -- password
      // belongs to the separate Change Password tab (its own button/flow below) and must
      // never be smuggled in here. The one exception is 'create' mode, which has no
      // separate password tab at all (Set Password lives inside this same single form),
      // so newPassword is the only place that value can come from there.
      const dataToSave = {
        ...profile,
        ...(mode === 'create' && passwords.newPassword && { newPassword: passwords.newPassword }),
      };
      await onSave(dataToSave);
      setSuccess('Profile updated successfully!');
      showToast('Profile updated successfully!', 'success', 3000);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save profile';
      setError(message);
      showToast(message, 'error', 4000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) return;

    setIsLoading(true);
    setError(null);
    try {
      // The backend has no change-password endpoint yet (users/change-password
      // is not routed), so never report success for a change that didn't happen.
      const message = 'Changing your password is not available yet. Please contact support.';
      setError(message);
      showToast(message, 'warning', 4000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      setError(message);
      showToast(message, 'error', 4000);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className={`${tc.layout.card} border ${tc.layout.cardBorder} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className={`flex items-center justify-between gap-4 px-6 py-5 border-b ${tc.layout.cardBorder}`}>
          <h2 className={`text-lg font-bold ${tc.layout.text}`}>
            {mode === 'create' ? 'Create New User' : 'Account Settings'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-base transition ${tc.layout.textSecondary} ${tc.dropdown.hoverBg}`}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${tc.layout.cardBorder}`}>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'profile'
                ? `${tc.accent.primaryText} border-b-2 border-amber-500`
                : `${tc.layout.textSecondary} hover:${tc.layout.text}`
            }`}
          >
            Profile Information
          </button>
          {mode === 'view' && (
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'password'
                  ? `${tc.accent.primaryText} border-b-2 border-amber-500`
                  : `${tc.layout.textSecondary} hover:${tc.layout.text}`
              }`}
            >
              Change Password
            </button>
          )}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {/* Status Messages */}
          {error && (
            <div className="mb-4 px-3 py-2 text-sm bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 px-3 py-2 text-sm bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
              {success}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-4">
              {/* Profile Picture */}
              <div>
                <label className={labelClsLg}>
                  Profile Picture
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-amber-500/15 rounded-full flex items-center justify-center overflow-hidden">
                    {profilePicPreview ? (
                      <img
                        src={profilePicPreview}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-3xl">👤</span>
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      id="profilePic"
                      onChange={handleProfilePicChange}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      onClick={() =>
                        document.getElementById('profilePic')?.click()
                      }
                      className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-sm"
                    >
                      Upload Photo
                    </button>
                    <p className={hintCls}>
                      JPG, PNG up to 5MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Username */}
              {mode === 'view' ? (
                <div>
                  <label className={labelCls}>
                    Username (Read-only)
                  </label>
                  <input
                    type="text"
                    value={profile.username}
                    readOnly
                    className={inputReadonlyCls}
                  />
                  <p className={hintCls}>
                    Username cannot be changed
                  </p>
                </div>
              ) : (
                <div>
                  <label className={labelCls}>
                    Username *
                  </label>
                  <input
                    type="text"
                    value={profile.username}
                    onChange={(e) =>
                      handleProfileChange('username', e.target.value)
                    }
                    placeholder="Enter username"
                    className={inputCls}
                  />
                </div>
              )}

              {/* Email */}
              {mode === 'view' ? (
                <div>
                  <label className={labelCls}>
                    Email (Read-only)
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    readOnly
                    className={inputReadonlyCls}
                  />
                </div>
              ) : (
                <div>
                  <label className={labelCls}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) =>
                      handleProfileChange('email', e.target.value)
                    }
                    placeholder="Enter email address"
                    className={inputCls}
                  />
                </div>
              )}

              {/* First Name and Last Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={profile.firstName}
                    onChange={(e) =>
                      handleProfileChange('firstName', e.target.value)
                    }
                    placeholder="John"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={profile.lastName}
                    onChange={(e) =>
                      handleProfileChange('lastName', e.target.value)
                    }
                    placeholder="Doe"
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Phone and DOB */}
              <div className="grid grid-cols-2 gap-4">
                {/* Phone Number */}
                {mode === 'view' ? (
                  <div>
                    <label className={labelCls}>
                      Phone Number (Read-only)
                    </label>
                    <input
                      type="tel"
                      value={profile.phone}
                      readOnly
                      className={inputReadonlyCls}
                    />
                  </div>
                ) : (
                  <div>
                    <label className={labelCls}>
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) =>
                        handleProfileChange('phone', e.target.value)
                      }
                      placeholder="+1 234 567 8900"
                      className={inputCls}
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls}>
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    value={profile.dob}
                    onChange={(e) =>
                      handleProfileChange('dob', e.target.value)
                    }
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Gender and Class/Standard */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    Gender *
                  </label>
                  <select
                    value={profile.gender}
                    onChange={(e) =>
                      handleProfileChange('gender', e.target.value)
                    }
                    className={selectCls}
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>
                    Class/Standard *
                  </label>
                  <select
                    value={profile.classOrStandard}
                    onChange={(e) =>
                      handleProfileChange('classOrStandard', e.target.value)
                    }
                    className={selectCls}
                  >
                    <option value="">Select Class/Standard</option>
                    <option value="9">Class 9</option>
                    <option value="10">Class 10</option>
                    <option value="11">Class 11</option>
                    <option value="12">Class 12</option>
                    <option value="undergrad">Undergraduate</option>
                    <option value="postgrad">Postgraduate</option>
                  </select>
                </div>
              </div>

              {/* Address */}
              <div>
                <label className={labelCls}>
                  Address *
                </label>
                <textarea
                  value={profile.address}
                  onChange={(e) =>
                    handleProfileChange('address', e.target.value)
                  }
                  placeholder="Street address"
                  rows={3}
                  className={inputCls}
                />
              </div>

              {/* City, State, Zip Code */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>
                    City
                  </label>
                  <input
                    type="text"
                    value={profile.city}
                    onChange={(e) =>
                      handleProfileChange('city', e.target.value)
                    }
                    placeholder="New York"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    State
                  </label>
                  <input
                    type="text"
                    value={profile.state}
                    onChange={(e) =>
                      handleProfileChange('state', e.target.value)
                    }
                    placeholder="NY"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Zip Code
                  </label>
                  <input
                    type="text"
                    value={profile.zipCode}
                    onChange={(e) =>
                      handleProfileChange('zipCode', e.target.value)
                    }
                    placeholder="10001"
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Country */}
              <div>
                <label className={labelCls}>
                  Country
                </label>
                <input
                  type="text"
                  value={profile.country}
                  onChange={(e) =>
                    handleProfileChange('country', e.target.value)
                  }
                  placeholder="United States"
                  className={inputCls}
                />
              </div>

              {/* Password Section - Show in create mode only */}
              {mode === 'create' && (
                <>
                  <div className={`border-t ${tc.layout.cardBorder} pt-6`}>
                    <h3 className={`text-base font-semibold ${tc.layout.text} mb-3`}>Set Password</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>
                        Password *
                      </label>
                      <input
                        type="password"
                        value={passwords.newPassword}
                        onChange={(e) =>
                          handlePasswordChange('newPassword', e.target.value)
                        }
                        placeholder="Enter password"
                        className={inputCls}
                      />
                      <p className={hintCls}>Minimum 6 characters</p>
                    </div>
                    <div>
                      <label className={labelCls}>
                        Confirm Password *
                      </label>
                      <input
                        type="password"
                        value={passwords.confirmPassword}
                        onChange={(e) =>
                          handlePasswordChange('confirmPassword', e.target.value)
                        }
                        placeholder="Confirm password"
                        className={inputCls}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {/* Password Tab - Only show in view mode */}
          {mode === 'view' && activeTab === 'password' && (
            <div className="space-y-4 max-w-md">
              <div>
                <label className={labelCls}>
                  Current Password *
                </label>
                <input
                  type="password"
                  value={passwords.currentPassword}
                  onChange={(e) =>
                    handlePasswordChange('currentPassword', e.target.value)
                  }
                  placeholder="Enter your current password"
                  className={inputCls}
                />
              </div>
              
              <div>
                <label className={labelCls}>
                  New Password *
                </label>
                <input
                  type="password"
                  value={passwords.newPassword}
                  onChange={(e) =>
                    handlePasswordChange('newPassword', e.target.value)
                  }
                  placeholder="Enter new password"
                  className={inputCls}
                />
                <p className={hintCls}>
                  Minimum 6 characters
                </p>
              </div>

              <div>
                <label className={labelCls}>
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={passwords.confirmPassword}
                  onChange={(e) =>
                    handlePasswordChange('confirmPassword', e.target.value)
                  }
                  placeholder="Confirm new password"
                  className={inputCls}
                />
              </div>

              <div className="pt-4">
                <button
                  onClick={handleChangePassword}
                  disabled={isLoading}
                  className="w-full px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition font-medium"
                >
                  {isLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-3 px-6 py-4 border-t ${tc.layout.cardBorder} ${tc.layout.contentBackground}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 text-sm border rounded-lg ${tc.layout.cardBorder} ${tc.layout.textSecondary} ${tc.dropdown.hoverBg} transition`}
          >
            Cancel
          </button>
          {activeTab === 'profile' && (
            <button
              onClick={handleSaveProfile}
              disabled={isLoading}
              className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition font-medium"
            >
              {isLoading ? (mode === 'create' ? 'Creating...' : 'Saving...') : (mode === 'create' ? 'Create User' : 'Save Changes')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
